import {Box, Tab, Tabs, Typography} from '@mui/material';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './BulkFolderUnFolder.scss';
import {TabData, TabPanelProps} from '../../user-management/edit-user/EditUser';
import {useEffect, useMemo, useState} from 'react';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {
  FIRST_LAST_NAME_REGEX,
  MAX_FOLDER_NAME_LENGTH,
  RoleId,
  transition_time_default,
  PROPOGATION_TYPE,
  existingObjErrCode,
  ToastType,
} from '../../../shared/constants';
import ExistingFolder from './existing-folder/ExistingFolder';
import NewFolder from './new-folder/NewFolder';
import {RootState} from '../../../redux/store';
import {useSelector} from 'react-redux';
import {ISelectItem} from '../../../shared/types';
import {useGetBulkTagSelectedDocCount} from '../../../hooks/assignments/useAssignments';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {useAddFolder} from '../../../hooks/tagsFoldering/useTagsFoldering';
import {IAddFolderRequest} from '../../../api/types/tagFoldering.type';
import {numberFormatter} from '../../../utils/utils';
import {ActionSelection} from '../../doc-explorer/ViewDocExplorer';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ToastMessageConfigType} from '../../../api/types/ingestionApi.type';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className="tabpanel"
      {...other}
      data-testid={`bulk-folder-tab-panel-${index}`}
    >
      {value === index && (
        <div data-testid={`bulk-folder-tab-panel-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}

const BulkFolderUnFolder: React.FC<{
  isOpen: boolean;
  setOpenActionMenu: (val: boolean) => void;
  handleBulkFolderContinue: (
    val: string,
    docCount?: number,
    newFolderId?: number,
    validIds?: string[]
  ) => void;
  isNonbg?: boolean;
  masterDocListId: number;
  setSelectedNewExistingFolder: (value: ActionSelection | null) => void;
  nonBgStandardCount?: number;
}> = ({
  isOpen,
  setOpenActionMenu,
  handleBulkFolderContinue,
  isNonbg,
  masterDocListId,
  setSelectedNewExistingFolder,
  nonBgStandardCount,
}) => {
  const enBulkFolder = 'actionMenu.bulkFolderFlyout';
  const radioOptions = [
    {
      label: t(`${enBulkFolder}.folderDocs`),
      value: 'folderDocs',
    },
    {
      label: t(`${enBulkFolder}.unfolderDocs`),
      value: 'unfolderDocs',
    },
  ];

  const {mutate: addFolder, isPending: isAddFolderLoading} = useAddFolder(
    (data) => {
      setOpenActionMenu(true);
      const error = data?.errorText;
      if (error?.includes(existingObjErrCode)) setIsFolderExist(true);
      else {
        handleBulkFolderContinue(
          selectedAction,
          totalSelectedDocs,
          data.folderId
        );
      }
    }
  );

  const [tabValue, setTabValue] = useState(0);
  const [selectedAction, setSelectedAction] = useState('folderDocs');
  const [roleId, setRoleId] = useState<number>();
  const [selectedExistingFolders, setSelectedExistingFolders] = useState<
    string[] | null
  >();
  const [selectedNewFolder, setSelectedNewFolder] = useState<string | null>();
  const [searchInputExistingFolders, setSearchInputExistingFolders] =
    useState<string>('');
  const [searchInputNewFolders, setSearchInputNewFolders] =
    useState<string>('');
  const [name, setName] = useState<string>('');
  const [isNameValid, setIsNameValid] = useState<boolean>(true);
  const [isNameMaxLengthValid, setIsNameMaxLengthValid] =
    useState<boolean>(true);
  const [touchedFields, setTouchedFields] = useState<{
    name: boolean;
  }>({
    name: false,
  });
  const [propagateFolder, setPropagateFolder] = useState<ISelectItem[]>([]);
  const [totalSelectedDocs, setTotalSelectedDocs] = useState<number>(0);
  const [securityGroupId, setSecurityGroupId] = useState<number>(0);
  const [projectId, setProjectId] = useState<number>(0);
  const [isFolderExist, setIsFolderExist] = useState<boolean>(false);
  const [folderTreeData, setFolderTreeData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const shouldFetchCount = !isNonbg && masterDocListId > 0;

  const {data, isFetching} = useGetBulkTagSelectedDocCount(
    {masterDocListId},
    shouldFetchCount
  );

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setName(value);
    setIsFolderExist(false);
    if (value.trim() !== '') {
      const isValid = FIRST_LAST_NAME_REGEX.test(value.trim());
      setIsNameValid(isValid);
      setIsNameMaxLengthValid(value.length <= MAX_FOLDER_NAME_LENGTH);
    } else {
      setIsNameValid(false);
      setIsNameMaxLengthValid(true);
    }
  };
  const handleBlurName = (fieldName: keyof typeof touchedFields) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };
  const setErrorMsg = () => {
    if (touchedFields.name && !name) {
      return t(`${enBulkFolder}.errors.nameRequired`);
    }
    if (!isNameValid && name) {
      return t(`${enBulkFolder}.errors.nameValidationMsg`);
    }
    if (!isNameMaxLengthValid) {
      return t(`${enBulkFolder}.errors.maxLengthMsg`, {
        max: MAX_FOLDER_NAME_LENGTH,
      });
    }
    if (isFolderExist) {
      return t(`${enBulkFolder}.errors.nameExist`);
    }
    return '';
  };

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  useEffect(() => {
    if (loginUserData) {
      setRoleId(loginUserData?.roleId);
      setSecurityGroupId(loginUserData?.securityGroupId);
      setProjectId(loginUserData?.projectId);
    }
  }, [loginUserData]);

  useEffect(() => {
    setSelectedAction('folderDocs');
  }, []);

  useEffect(() => {
    if (tabData.length === 1) {
      setTabValue(0);
    }
  }, [selectedAction]);

  useEffect(() => {
    if (!isNonbg) {
      if (data?.docCount !== undefined && data.isLikeDocListGenerated) {
        setTotalSelectedDocs(data.docCount);
      }
    } else if (isNonbg && nonBgStandardCount !== undefined) {
      setTotalSelectedDocs(nonBgStandardCount);
    }
  }, [isNonbg, data?.docCount, data?.isLikeDocListGenerated, nonBgStandardCount]);

  useEffect(() => {
    if (selectedAction === 'unFolderDocs' || tabValue === 0) {
      if (selectedExistingFolders && selectedExistingFolders.length > 0) {
        setSelectedNewExistingFolder({
          type: 'existing',
          ids: selectedExistingFolders,
        });
      } else {
        setSelectedNewExistingFolder(null);
      }
    } else if (tabValue === 1) {
      if (selectedNewFolder) {
        setSelectedNewExistingFolder({type: 'new', ids: [selectedNewFolder]});
      } else {
        setSelectedNewExistingFolder(null);
      }
    }
  }, [selectedAction, tabValue, selectedExistingFolders, selectedNewFolder]);

  const isNameInputValid =
    !isNameValid ||
    !isNameMaxLengthValid ||
    (touchedFields.name && !name) ||
    isFolderExist;
  const tabData: TabData[] = [
    {
      id: 'existing-folder',
      label: (
        <Typography
          className="typo-text subhead5_size tab-label"
          data-testid="existing-folder-label"
        >
          {t(`${enBulkFolder}.existingFolder`)}
        </Typography>
      ),
      content: (
        <ExistingFolder
          selectedExistingFolders={selectedExistingFolders || []}
          setSelectedExistingFolders={setSelectedExistingFolders}
          searchInputExistingFolders={searchInputExistingFolders}
          setSearchInputExistingFolders={setSearchInputExistingFolders}
          setFolderTreeData={setFolderTreeData}
        />
      ),
    },
    {
      id: 'new-folder',
      label: (
        <Box className="functionality-tab-label">
          <Typography
            className="typo-text subhead5_size tab-label"
            data-testid="new-folder-label"
          >
            {t(`${enBulkFolder}.newFolder`)}
          </Typography>
        </Box>
      ),
      content: (
        <NewFolder
          selectedNewFolder={selectedNewFolder || ''}
          setSelectedNewFolder={setSelectedNewFolder}
          searchInputNewFolders={searchInputNewFolders}
          setSearchInputNewFolders={setSearchInputNewFolders}
          name={name}
          handleChangeName={handleChangeName}
          isNameInputValid={isNameInputValid}
          handleBlurName={handleBlurName}
          setErrorMsg={setErrorMsg}
          propagateFolder={propagateFolder}
          setPropagateFolder={setPropagateFolder}
        />
      ),
    },
  ];
  const handleChange = (event: React.SyntheticEvent, newTabValue: number) => {
    setTabValue(newTabValue);
  };

  const isContinue = () => {
    if (isAddFolderLoading) return false;
    if (totalSelectedDocs <= 0) return false;
    if (selectedAction === 'unfolderDocs' || tabValue === 0) {
      return Boolean(
        selectedExistingFolders && selectedExistingFolders.length > 0
      );
    } else if (tabValue === 1) {
      return Boolean(selectedNewFolder && !isNameInputValid && name);
    }
    return false;
  };

  const folderMap = useMemo(() => {
    const map = new Map<string, {isGroup: boolean; hasChildren: boolean}>();

    const traverse = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      nodes.forEach((node) => {
        const hasChildren = !!node.children?.length;

        map.set(node.id, {
          isGroup: !!node.isGroup,
          hasChildren,
        });

        if (node.children) traverse(node.children);
      });
    };

    traverse(folderTreeData);
    return map;
  }, [folderTreeData]);

  const handleContinue = () => {
    if (tabValue === 1 && selectedAction === 'folderDocs') {
      const selectedIds = new Set(propagateFolder.map((item) => item.id));

      const newPropagation = {
        propagationToFamily: selectedIds.has(PROPOGATION_TYPE.FAMILY),
        propagationToDuplicates: selectedIds.has(PROPOGATION_TYPE.DUPLICATES),
        propagationToEmailThreads: selectedIds.has(
          PROPOGATION_TYPE.EMAILTHREADS
        ),
        propagationToEmailDuplicates: selectedIds.has(
          PROPOGATION_TYPE.EMAILDUPLICATES
        ),
      };

      const payload: IAddFolderRequest = {
        name: name.trim(),
        isGroup: false,
        masterDocListID: isNonbg ? 0 : masterDocListId,
        securityGroupID: securityGroupId.toString(),
        parentID: Number(selectedNewFolder ?? 0),
        projectID: Number(projectId ?? 0),
        isLocked: false,
        count: 0,
        ...newPropagation,
      };

      addFolder(payload);
    } else {
      const validIds: string[] = [];
      selectedExistingFolders?.forEach((id) => {
        const folder = folderMap.get(id);
        if (!folder) return;
        // INVALID: group without children
        const isInvalid = folder.isGroup && !folder.hasChildren;

        if (!isInvalid) {
          validIds.push(id);
        }
      });

      if (validIds.length === 0) {
        if (selectedAction === 'folderDocs') {
          setToastMsgConfig({
            show: true,
            title: t('common.warning'),
            type: ToastType.Warning,
            message: t(`${enBulkFolder}.emptyGroupValidationForFolder`),
          });
        } else {
          setToastMsgConfig({
            show: true,
            title: t('common.warning'),
            type: ToastType.Warning,
            message: t(`${enBulkFolder}.emptyGroupValidationForUnFolder`),
          });
        }
        return;
      }

      setSelectedNewExistingFolder({
        type: 'existing',
        ids: validIds,
      });
      handleBulkFolderContinue(
        selectedAction,
        totalSelectedDocs,
        undefined,
        validIds
      );
    }
  };

  return (
    <>
      {isOpen && (
        <ArrDrawer
          transition={transition_time_default}
          showButton={false}
          isOpen={isOpen}
          size="large"
          side="right"
          title=""
          showHeader={false}
          ariaLabel={t(`${enBulkFolder}.flyoutName`)}
          className="bulk-folder-flyout"
          onClose={() => {
            setOpenActionMenu(false);
          }}
        >
          <Box className="bulk-folder-header-box">
            <Box className="title-box">
              <Typography
                className=" typo-text headline3_size title"
                data-testid="flyout-title"
                tabIndex={0}
              >
                {t(`${enBulkFolder}.flyoutTitle`)}
              </Typography>
            </Box>
            <Box className="action-buttons-box">
              <ArrButton
                variant="secondary"
                size="medium"
                onClick={() => {
                  setOpenActionMenu(false);
                }}
                disabled={false}
                aria-label={t('common.cancel')}
                dataTestId="bulk-unfolder-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>
              <ArrButton
                variant="primary"
                className={isContinue() ? '' : 'btn-disabled'}
                size="medium"
                type="submit"
                aria-label={t('common.continue')}
                dataTestId="bulk-unfolder-continue-button"
                isAriaDisabled={!isContinue()}
                onClick={handleContinue}
                tabIndex={0}
              >
                {t('common.continue')}
              </ArrButton>
            </Box>
          </Box>
          <Box className="bulk-folder-flyout-content">
            <Box className="folder-unfolder-selection-box">
              <Box className="total-docs" tabIndex={0}>
                <Typography
                  component="span"
                  className="typo-text subhead4_size title"
                  data-testid="total-docs-desc"
                >
                  {t(`${enBulkFolder}.totalDocs`)}&nbsp;
                </Typography>
                <Typography
                  component="span"
                  className="typo-text subhead4_size count"
                  data-testid="total-docs"
                >
                  {isFetching ||
                  (!data?.isLikeDocListGenerated &&
                    nonBgStandardCount === undefined) ? (
                    <ArrLoadingSpinner className="arr-loading-small" />
                  ) : (
                    numberFormatter.format(totalSelectedDocs)
                  )}
                </Typography>
              </Box>
              <Box className="radio-selection-box">
                <ArrRadio
                  dataTestId="bulk-tag-untag"
                  name="controlled-radio-buttons-group"
                  size="small"
                  onChange={(value) => {
                    setSelectedAction(value);
                  }}
                  value={selectedAction}
                  data={radioOptions}
                />
              </Box>
            </Box>
            <Box className="tabs-section">
              {roleId?.toString() === RoleId.Reviewer ||
              selectedAction === 'unfolderDocs' ? (
                <div
                  className="unfolder-docs-tab-panel"
                  data-testid="bulk-folder-un-folder-div-button"
                >
                  <ExistingFolder
                    selectedExistingFolders={selectedExistingFolders || []}
                    setSelectedExistingFolders={setSelectedExistingFolders}
                    searchInputExistingFolders={searchInputExistingFolders}
                    setSearchInputExistingFolders={
                      setSearchInputExistingFolders
                    }
                    setFolderTreeData={setFolderTreeData}
                  />
                </div>
              ) : (
                <>
                  <Tabs
                    value={tabValue || 0}
                    onChange={handleChange}
                    aria-label="tabs"
                    className="tabs"
                  >
                    {tabData.map((tab) => (
                      <Tab
                        id={tab.id}
                        aria-controls={`tabpanel-${tab.id}`}
                        key={tab.id}
                        label={tab.label}
                        className="tab"
                        data-testid={tab.id}
                      />
                    ))}
                  </Tabs>
                  {tabData.map((tab, index) => (
                    <TabPanel key={tab.id} value={tabValue || 0} index={index}>
                      {tab.content}
                    </TabPanel>
                  ))}
                </>
              )}
            </Box>
          </Box>
          {toastMsgConfig.show && (
            <ArrToastNotification
              onClose={() => setToastMsgConfig({show: false})}
              status={[toastMsgConfig.type ?? ToastType.Success]}
              message={toastMsgConfig.message ?? ''}
              title={toastMsgConfig.title}
            />
          )}
        </ArrDrawer>
      )}
    </>
  );
};

export default BulkFolderUnFolder;
